import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MonitoringService } from '../../../features/monitoring/monitoring.service';
import { Watchlist } from '../../../features/monitoring/monitoring.model';

export interface AddToWatchlistData {
  companies: Array<{
    entityid: string;
    entity_name: string;
    entity_gics?: string;
    isin?: string;
    companytype?: string;
    gsubind?: string | string[];
    gsubind_extended?: string;
  }>;
}

@Component({
  selector: 'app-add-to-watchlist-popup',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './add-to-watchlist-popup.component.html',
  styleUrl: './add-to-watchlist-popup.component.css'
})
export class AddToWatchlistPopupComponent implements OnInit {
  watchlists: Watchlist[] = [];
  selectedWatchlistIds: Set<number> = new Set(); // Multi-select support
  createNew: boolean = false;
  newWatchlistName: FormControl = new FormControl('', [Validators.required]);
  loading: boolean = false;
  successMessage: string | null = null;
  errorMessage: string | null = null;
  companiesInWatchlists: Map<number, Set<string>> = new Map(); // watchlistId -> Set of entityids
  hasIneligibleCompanies: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<AddToWatchlistPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddToWatchlistData,
    private monitoringService: MonitoringService
  ) {}

  ngOnInit(): void {
    this.checkEligibility();
    this.loadWatchlists();
  }

  checkEligibility(): void {
    // Check if any company is not eligible (not public or no GICS)
    this.hasIneligibleCompanies = this.data.companies.some(company => {
      const isPublic = company.companytype === 'Public Company' || company.companytype === 'Public Investment Firm';
      const hasGICS = !!(company.gsubind || company.gsubind_extended || company.entity_gics);
      return !isPublic || !hasGICS;
    });
  }

  loadWatchlists(): void {
    const userId = Number(localStorage.getItem('user_id'));
    if (!userId) {
      this.errorMessage = 'User not found';
      return;
    }

    this.monitoringService.getUserWatchlists(userId).subscribe({
      next: (res) => {
        this.watchlists = res.payload || [];
        this.checkCompaniesInWatchlists();
      },
      error: (err) => {
        this.errorMessage = 'Failed to load watchlists';
        console.error('Error loading watchlists:', err);
      }
    });
  }

  checkCompaniesInWatchlists(): void {
    // Use bulk endpoint to get all entityids at once (more efficient)
    const userId = Number(localStorage.getItem('user_id'));
    if (!userId) return;

    this.monitoringService.getAllWatchlistsEntityIds(userId).subscribe({
      next: (res) => {
        if (res.payload) {
          Object.entries(res.payload).forEach(([watchlistIdStr, entityids]) => {
            const watchlistId = Number(watchlistIdStr);
            this.companiesInWatchlists.set(watchlistId, new Set(entityids));
          });
        }
      },
      error: () => {
        // Silently fail - just won't show "already in watchlist" indicator
      }
    });
  }

  isCompanyInWatchlist(watchlistId: number, entityId: string): boolean {
    const entityIds = this.companiesInWatchlists.get(watchlistId);
    return entityIds ? entityIds.has(entityId) : false;
  }

  hasAnyCompanyInWatchlist(watchlistId: number): boolean {
    return this.data.companies.some(company => 
      this.isCompanyInWatchlist(watchlistId, company.entityid)
    );
  }

  toggleWatchlistSelection(watchlistId: number, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.selectedWatchlistIds.has(watchlistId)) {
      this.selectedWatchlistIds.delete(watchlistId);
    } else {
      this.selectedWatchlistIds.add(watchlistId);
      this.createNew = false;
      this.newWatchlistName.reset();
    }
  }

  isWatchlistSelected(watchlistId: number): boolean {
    return this.selectedWatchlistIds.has(watchlistId);
  }

  selectCreateNew(): void {
    this.createNew = true;
    this.selectedWatchlistIds.clear();
    this.newWatchlistName.reset();
  }

  canAdd(): boolean {
    if (this.createNew) {
      return this.newWatchlistName.valid && this.newWatchlistName.value?.trim().length > 0;
    }
    return this.selectedWatchlistIds.size > 0;
  }

  onAdd(): void {
    if (!this.canAdd()) return;

    this.loading = true;
    this.successMessage = null;
    this.errorMessage = null;

    if (this.createNew) {
      this.createWatchlistAndAdd();
    } else {
      this.addToExistingWatchlists();
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }

  createWatchlistAndAdd(): void {
    const watchlistName = this.newWatchlistName.value?.trim();
    if (!watchlistName) return;

    this.monitoringService.createWatchlist({ watchlist_name: watchlistName }).subscribe({
      next: (res) => {
        // Handle both array and single object responses
        const watchlist = Array.isArray(res.payload) ? res.payload[0] : (res.payload as any);
        const newWatchlistId = watchlist?.watchlist_id;
        if (newWatchlistId) {
          this.addCompaniesToWatchlist(newWatchlistId, watchlistName, true).then((result) => {
            this.loading = false;
            if (this.data.companies.length === 1) {
              this.successMessage = `Created '${watchlistName}' and added the company.`;
            } else {
              this.successMessage = `Created '${watchlistName}' and added ${result.added} companies. ${result.alreadyExisted} already existed.`;
            }
          }).catch(() => {
            this.loading = false;
            this.errorMessage = 'Failed to add companies to watchlist';
          });
        } else {
          this.loading = false;
          this.errorMessage = 'Failed to create watchlist';
        }
      },
      error: (err) => {
        this.loading = false;
        this.errorMessage = 'Failed to create watchlist';
        console.error('Error creating watchlist:', err);
      }
    });
  }

  addToExistingWatchlists(): void {
    if (this.selectedWatchlistIds.size === 0) return;
    
    // Add to all selected watchlists using bulk endpoint (one call per watchlist)
    const watchlistIds = Array.from(this.selectedWatchlistIds);
    const addPromises = watchlistIds.map(watchlistId => {
      const watchlist = this.watchlists.find(w => w.watchlist_id === watchlistId);
      return this.addCompaniesToWatchlist(watchlistId, watchlist?.watchlist_name || 'watchlist', false);
    });

    Promise.all(addPromises).then(results => {
      this.loading = false;
      const watchlistNames = watchlistIds
        .map(id => this.watchlists.find(w => w.watchlist_id === id)?.watchlist_name)
        .filter(Boolean);
      
      // Check for errors
      const errors = results.filter(r => r.error);
      const successes = results.filter(r => !r.error);
      
      if (errors.length > 0) {
        // Some watchlists failed
        const errorMessages = errors.map((r) => {
          const resultIndex = results.indexOf(r);
          const watchlistId = watchlistIds[resultIndex];
          const watchlistName = this.watchlists.find(w => w.watchlist_id === watchlistId)?.watchlist_name || 'Unknown';
          return `${watchlistName}: ${r.error}`;
        });
        
        if (successes.length === 0) {
          // All failed
          this.errorMessage = errorMessages.join('; ');
          this.successMessage = null;
        } else {
          // Partial success
          this.successMessage = `Added to ${successes.length} watchlist(s).`;
          this.errorMessage = `Failed for ${errors.length} watchlist(s): ${errorMessages.join('; ')}`;
        }
      } else {
        // All succeeded
        if (watchlistIds.length === 1) {
          const watchlistName = watchlistNames[0];
          const result = results[0];
          
          if (this.data.companies.length === 1) {
            if (result.alreadyExisted > 0) {
              this.successMessage = `Company already exists in '${watchlistName}'.`;
            } else {
              this.successMessage = `Successfully added to '${watchlistName}'.`;
            }
          } else {
            this.successMessage = `Added ${result.added} companies to '${watchlistName}'.`;
          }
        } else {
          this.successMessage = `Successfully added to ${watchlistIds.length} watchlists.`;
        }
      }
    }).catch(() => {
      this.loading = false;
      this.errorMessage = 'Failed to add companies to watchlists';
    });
  }

  addCompaniesToWatchlist(watchlistId: number, watchlistName: string, isNew: boolean): Promise<{added: number, alreadyExisted: number, error?: string}> {
    // Check which companies were already in the watchlist before adding
    const existingEntityIds = this.companiesInWatchlists.get(watchlistId) || new Set<string>();
    const companiesAlreadyInWatchlist = this.data.companies.filter(c => 
      existingEntityIds.has(c.entityid)
    );

    // Prepare companies data for bulk add
    const companiesToAdd = this.data.companies.map(company => ({
      entityid: company.entityid,
      entity_name: company.entity_name,
      entity_gics: company.gsubind_extended || company.gsubind || company.entity_gics,
      isin: company.isin
    }));

    // Use bulk endpoint - one call per watchlist instead of one per company
    return this.monitoringService.addCompaniesToWatchlist(watchlistId, companiesToAdd).toPromise()
      .then((res: any) => {
        // Success case - backend returns added_count
        const addedCount = res?.payload?.added_count || 0;
        const totalRequested = companiesToAdd.length;
        const alreadyExisted = totalRequested - addedCount;
        const newlyAdded = addedCount;

        // Update the companies in watchlist map
        const entityIds = new Set(this.data.companies.map(c => c.entityid));
        const existing = this.companiesInWatchlists.get(watchlistId) || new Set();
        entityIds.forEach(id => existing.add(id));
        this.companiesInWatchlists.set(watchlistId, existing);
        return { added: newlyAdded, alreadyExisted:alreadyExisted };
      })
      .catch((err: any) => {
        // Handle HTTP errors (400, 500, etc.)
        // Angular HTTP errors have structure: err.error contains response body
        const errorResponse = err?.error || err;
        const errorMsg = errorResponse?.message || err?.message || 'Failed to add companies to watchlist';
        
        // Check if it's a limit exceeded error
        if (err?.status === 400 || errorMsg.includes('exceed') || errorMsg.includes('limit')) {
          return { added: 0, alreadyExisted: companiesAlreadyInWatchlist.length, error: errorMsg };
        }
        
        return { added: 0, alreadyExisted: companiesAlreadyInWatchlist.length, error: errorMsg };
      });
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}

